import { useEffect, useState } from "react";
import { toast } from "sonner";
import { KeyRound, Pencil, Plus, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import api, { apiError } from "./api";

const ROLES = [
  ["admin", "Admin"],
  ["qc", "QC"],
  ["commercial", "Commercial"],
  ["unit_head", "Unit Head"],
  ["accounts", "Accounts"],
];

const RolePicker = ({ value, onChange, disabledSelf }) => (
  <div className="flex flex-wrap gap-3">
    {ROLES.map(([k, label]) => (
      <label key={k} className="flex items-center gap-2 text-sm">
        <Checkbox
          data-testid={`qc-user-role-${k}`}
          checked={value.includes(k)}
          onCheckedChange={(v) => onChange(v ? [...value, k] : value.filter((r) => r !== k))}
        />
        {label}
      </label>
    ))}
  </div>
);

export default function Users() {
  const [users, setUsers] = useState(null);
  const [dialog, setDialog] = useState({ open: false, initial: null });
  const [resetFor, setResetFor] = useState(null);
  const [form, setForm] = useState({ name: "", email: "", password: "", roles: [] });
  const [resetMode, setResetMode] = useState("generate");
  const [resetPw, setResetPw] = useState("");
  const [resetResult, setResetResult] = useState(null);

  const load = () => api.get("/users").then((r) => setUsers(r.data)).catch((e) => toast.error(apiError(e)));
  useEffect(() => {
    load();
  }, []);

  const openDialog = (initial) => {
    setDialog({ open: true, initial });
    setForm(initial
      ? { name: initial.name, email: initial.email, password: "", roles: initial.roles }
      : { name: "", email: "", password: "", roles: [] });
  };

  const save = async () => {
    try {
      if (dialog.initial) {
        await api.put(`/users/${dialog.initial.id}`, { name: form.name, email: form.email, roles: form.roles });
        toast.success("User updated");
      } else {
        await api.post("/users", form);
        toast.success("User created");
      }
      setDialog({ open: false, initial: null });
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const doReset = async () => {
    try {
      const { data } = await api.post(`/users/${resetFor.id}/reset-password`, {
        mode: resetMode,
        password: resetMode === "manual" ? resetPw : undefined,
      });
      setResetResult(data.new_password);
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete user "${u.name}"?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) {
      toast.error(apiError(e));
    }
  };

  return (
    <div data-testid="qc-users-page">
      <div className="flex items-end justify-between">
        <div>
          <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[#8A9A91]">Administration</p>
          <h1 className="mt-2 font-display text-3xl font-semibold tracking-tight">User Management</h1>
        </div>
        <Button data-testid="qc-users-add" onClick={() => openDialog(null)}
          className="rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
          <Plus className="mr-1.5 h-4 w-4" /> Add User
        </Button>
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-[#E8E6E1] bg-white">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Name</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Roles</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {(users || []).map((u) => (
              <TableRow key={u.id} data-testid={`qc-user-row-${u.id}`}>
                <TableCell className="font-medium">{u.name}</TableCell>
                <TableCell>{u.email}</TableCell>
                <TableCell>
                  <div className="flex flex-wrap gap-1.5">
                    {u.roles.map((r) => (
                      <span key={r} className="rounded-full bg-[#1C3F2B]/[0.07] px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wider">
                        {ROLES.find(([k]) => k === r)?.[1] || r}
                      </span>
                    ))}
                  </div>
                </TableCell>
                <TableCell className="text-right">
                  <button title="Reset password" data-testid={`qc-user-reset-${u.id}`}
                    onClick={() => { setResetFor(u); setResetResult(null); setResetMode("generate"); setResetPw(""); }}
                    className="rounded-full p-2 text-[#8A9A91] hover:bg-[#E5A93D]/15 hover:text-[#7a5b12]">
                    <KeyRound className="h-4 w-4" />
                  </button>
                  <button title="Edit" data-testid={`qc-user-edit-${u.id}`} onClick={() => openDialog(u)}
                    className="rounded-full p-2 text-[#8A9A91] hover:bg-[#1C3F2B]/5 hover:text-[#1C3F2B]">
                    <Pencil className="h-4 w-4" />
                  </button>
                  <button title="Delete" data-testid={`qc-user-delete-${u.id}`} onClick={() => remove(u)}
                    className="rounded-full p-2 text-[#8A9A91] hover:bg-red-50 hover:text-red-700">
                    <Trash2 className="h-4 w-4" />
                  </button>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialog.open} onOpenChange={(o) => setDialog((d) => ({ ...d, open: o }))}>
        <DialogContent data-testid="qc-user-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">{dialog.initial ? "Edit User" : "Add User"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label>Name</Label>
              <Input data-testid="qc-user-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} />
            </div>
            <div className="space-y-1.5">
              <Label>Email</Label>
              <Input data-testid="qc-user-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} />
            </div>
            {!dialog.initial && (
              <div className="space-y-1.5">
                <Label>Password</Label>
                <Input data-testid="qc-user-password" type="text" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} />
              </div>
            )}
            <div className="space-y-1.5">
              <Label>Roles (a person can hold several)</Label>
              <RolePicker value={form.roles} onChange={(roles) => setForm({ ...form, roles })} />
            </div>
            <Button data-testid="qc-user-save" onClick={save} className="w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
              Save user
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <Dialog open={!!resetFor} onOpenChange={(o) => !o && setResetFor(null)}>
        <DialogContent data-testid="qc-reset-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-xl">Reset password — {resetFor?.name}</DialogTitle>
          </DialogHeader>
          {resetResult ? (
            <div className="space-y-4">
              <p className="text-sm text-[#8A9A91]">
                Copy this password and share it with the user through a separate channel (phone or in person). It is never emailed.
              </p>
              <div className="flex items-center justify-between rounded-xl border border-[#E8E6E1] bg-[#F9F8F6] px-4 py-3">
                <code data-testid="qc-reset-result" className="text-base font-semibold tracking-wide">{resetResult}</code>
                <Button variant="outline" size="sm" onClick={() => { navigator.clipboard.writeText(resetResult); toast.success("Copied"); }}>
                  Copy
                </Button>
              </div>
              <Button onClick={() => setResetFor(null)} className="w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">Done</Button>
            </div>
          ) : (
            <div className="space-y-4">
              <div className="flex gap-2">
                {[["generate", "Generate strong password"], ["manual", "Type manually"]].map(([k, label]) => (
                  <button key={k} data-testid={`qc-reset-mode-${k}`} onClick={() => setResetMode(k)}
                    className={`flex-1 rounded-full px-3 py-2 text-sm font-medium transition-colors ${
                      resetMode === k ? "bg-[#1C3F2B] text-[#F9F8F6]" : "border border-[#E8E6E1] text-[#8A9A91]"
                    }`}>
                    {label}
                  </button>
                ))}
              </div>
              {resetMode === "manual" && (
                <Input data-testid="qc-reset-manual" type="text" placeholder="New password (min 8 chars)" value={resetPw} onChange={(e) => setResetPw(e.target.value)} />
              )}
              <Button data-testid="qc-reset-confirm" onClick={doReset} className="w-full rounded-full bg-[#1C3F2B] text-[#F9F8F6] hover:bg-[#26543a]">
                Reset password
              </Button>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
